import { useState } from "react";
import { CheckCircle2, Zap } from "lucide-react";
import { createStripeCheckout } from "@/lib/api";

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: "£4.99",
    period: "/month",
    description: "Accurate revenue reporting straight from HubSpot.",
    features: [
      "Accurate MRR & ARR calculation",
      "Revenue by client report",
      "Pipeline & deal drilldown",
      "Company-level breakdown",
      "Custom filters & date ranges",
      "ARR sync back to HubSpot",
      "Multi-portal support",
    ],
    highlight: false,
  },
  {
    id: "pro",
    name: "Pro",
    price: "£7.99",
    period: "/month",
    description: "Everything in Starter, plus your own revenue analyst on demand.",
    features: [
      "Everything in Starter",
      "Name Your Report — request any revenue report",
      "Custom cohort analysis",
      "Board-ready revenue breakdowns",
      "Bespoke retention views",
      "Delivered within 48 hours",
    ],
    highlight: true,
  },
];

export default function StripeCheckoutPage() {
  const [loading, setLoading] = useState(null); // "starter" | "pro" | null
  const [error, setError] = useState("");

  const handleSelectPlan = async (planId) => {
    setError("");
    setLoading(planId);
    try {
      const data = await createStripeCheckout(null, planId);
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to start checkout. Please try again.");
      setLoading(null);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#323c44] px-4 py-16">
      {/* Header */}
      <div className="mb-10 text-center">
        <div className="mb-3 flex items-center justify-center gap-2">
          <svg viewBox="0 0 36 36" className="h-9 w-9 flex-shrink-0">
            <rect width="36" height="36" rx="8" fill="#323c44" />
            <text x="18" y="25" fontFamily="system-ui,-apple-system,sans-serif" fontSize="13" fontWeight="800" textAnchor="middle">
              <tspan fill="#50e3c2">P</tspan>
              <tspan fill="#ffffff">IQ</tspan>
            </text>
          </svg>
          <span className="font-bold text-xl text-white">PortalIQ</span>
        </div>
        <h1 className="text-3xl font-bold text-white">Choose your plan</h1>
        <p className="mt-2 text-slate-400">Your HubSpot portal is connected. Subscribe to unlock your reports.</p>
      </div>

      {/* Plan cards */}
      <div className="flex w-full max-w-2xl flex-col gap-4 sm:flex-row">
        {PLANS.map((plan) => (
          <div
            key={plan.id}
            className={`relative flex flex-1 flex-col rounded-2xl border p-6 ${
              plan.highlight
                ? "border-[#57c1a9] bg-[#323c44] shadow-[0_0_40px_rgba(255,122,89,0.15)]"
                : "border-white/10 bg-[#323c44]"
            }`}
          >
            {plan.highlight && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="rounded-full bg-[#50e3c2] px-3 py-0.5 text-xs font-bold uppercase tracking-wider text-[#323c44]">
                  Most popular
                </span>
              </div>
            )}

            <div className="mb-4">
              <p className="text-sm font-semibold uppercase tracking-widest text-slate-400">{plan.name}</p>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-4xl font-bold text-white">{plan.price}</span>
                <span className="text-slate-400">{plan.period}</span>
              </div>
              <p className="mt-2 text-sm text-slate-400">{plan.description}</p>
            </div>

            <ul className="mb-6 flex-1 space-y-2">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#119996]" />
                  {f}
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={() => handleSelectPlan(plan.id)}
              disabled={loading !== null}
              className={`flex w-full items-center justify-center gap-2 rounded-lg py-3 text-sm font-semibold transition-all duration-150 ${
                plan.highlight
                  ? "bg-[#50e3c2] text-white hover:bg-[#57c1a9] disabled:opacity-60"
                  : "border border-white/20 text-white hover:bg-white/10 disabled:opacity-60"
              }`}
            >
              {loading === plan.id ? (
                "Redirecting to payment…"
              ) : (
                <>
                  {plan.highlight && <Zap className="h-4 w-4" />}
                  Get started with {plan.name}
                </>
              )}
            </button>
          </div>
        ))}
      </div>

      {error && (
        <p className="mt-4 rounded-lg bg-red-900/30 px-4 py-2 text-sm text-red-400">{error}</p>
      )}

      <p className="mt-6 text-xs text-slate-600">
        Secure payment via Stripe. Cancel anytime. No contracts.
      </p>
    </div>
  );
}
